<template>
    <div :class="[classes + '-wrapper', {additionalOptions: displayAdditionalOptions}]" class="border border-secondary">
        <div :class="`${classes}__player`" class="text-secondary">
            <oc-icon type="stop" @click="stop()" title="Stop" tag="a" class="oc-audio-btn" />
            <oc-icon type="play" v-if="paused" @click="play()" title="Play" :class="['oc-audio-btn']" tag="a" />
            <oc-icon type="pause" v-else @click="pause()" title="Pause" :class="['oc-audio-btn']" tag="a" />

            <div @click="setPosition($event)" :class="`${classes}__playback-time-wrapper`" title="Time played : Total time" :style="{cursor: allowTimeChange ? 'pointer' : 'default'}">
                <div :style="progressStyle" :class="`${classes}__playback-time-indicator`"></div>
                <span :class="`${classes}__playback-time-current`">{{currentTime}}</span>
                <span :class="`${classes}__playback-time-separator`"></span>
                <span :class="`${classes}__playback-time-total`">{{duration}}</span>
            </div>

            <div :class="`${classes}__extern-wrapper`" v-if="displayAdditionalOptions">
                <oc-icon @click="download()" type="download" tag="a" class="oc-audio-btn" style="border-left: solid 1px #6c757d;" />

                <oc-icon @click="changeLoop()" title="Repeat" :type="innerLoop ? 'refresh' : 'repeat'" tag="a" class="oc-audio-btn" />

                <oc-icon @click="mute()" title="Mute" :type="isMuted ? 'volume-up' : 'volume-off'" tag="a" class="oc-audio-btn" />

                <oc-icon @mouseover="toggleVolume()" type="signal" title="Volume" tag="a" class="volume-toggle oc-audio-btn border-right-0">
                    <input
                        orient="vertical"
                        v-model.lazy="volumeValue"
                        @change="updateVolume()"
                        v-show="hideVolumeSlider"
                        type="range"
                        min="0" max="100"
                        class="volume-slider"
                    />
                </oc-icon>
            </div>
        </div>
        <audio :id="playerId" :loop="innerLoop" ref="audiofile" :src="getUniqueFileName()" preload="auto" style="display:none;"></audio>
    </div>
</template>

<script>
export const baseVolumeValue = 90
let audio, uuid

export const prefixCls = 'oc-audio'

export const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx'.replace(/[xy]/g, function (c) {
        let v, r
        r = Math.random() * 16 | 0; v = c === 'x' ? r : (r & 0x3 | 0x8)
        return v.toString(16)
    })
}

export const convertTimeHHMMSS = (val) => {
    let hhmmss = new Date(val * 1000).toISOString().substr(11, 8)
    return (hhmmss.indexOf('00:') === 0) ? hhmmss.substr(3) : hhmmss
}

export default {
    name: 'oc-audio',
    props: {
        file: {
            type: String,
            default: null
        },
        autoPlay: {
            type: Boolean,
            default: false
        },
        loop: {
            type: Boolean,
            default: false
        },
        displayAdditionalOptions: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        duration() {
            return this.audio ? convertTimeHHMMSS(this.totalDuration) : ''
        },
        playerId() {
            return 'player-' + this.uuid
        },
        classes() {
            return prefixCls
        },
        allowTimeChange() {
            return this.file.indexOf('.wav') === -1
        }
    },
    data () {
        return {
            isMuted: false,
            loaded: false,
            playing: false,
            paused: true,
            progressStyle: '',
            currentTime: '00:00',
            uuid: '0',
            innerLoop: undefined,
            audio: undefined,
            totalDuration: 0,
            hideVolumeSlider: false,
            volumeValue: baseVolumeValue
        }
    },
    methods: {
        getUniqueFileName() {
            return this.file + '?' + this.uuid
        },
        setPosition(e) {
            let tag = e.currentTarget
            if (this.paused || ! this.allowTimeChange) return

            const pos = tag.getBoundingClientRect()
            const seekPos = (e.clientX - pos.left) / pos.width

            this.audio.currentTime = parseInt(this.audio.duration * seekPos)
        },
        updateVolume() {
            this.hideVolumeSlider = false
            this.audio.volume = this.volumeValue / 100
            if (this.volumeValue / 100 > 0) {
                this.isMuted = this.audio.muted = false
            }

            if (this.volumeValue / 100 === 0) {
                this.isMuted = this.audio.muted = true
            }
        },
        toggleVolume() {
            this.hideVolumeSlider = true
        },
        stop() {
            this.playing = false
            this.paused = true
            this.audio.pause()
            this.audio.currentTime = 0
        },
        play() {
            if (this.playing && !this.paused) return
            this.paused = false
            this.audio.play()
            this.playing = true
        },
        pause() {
            this.paused = !this.paused
            this.paused ? this.audio.pause() : this.audio.play()
        },
        changeLoop() {
            this.innerLoop = !this.innerLoop
        },
        download() {
            this.stop()
            window.open(this.getUniqueFileName(), 'download')
        },
        mute() {
            this.isMuted = !this.isMuted
            this.audio.muted = this.isMuted
            this.volumeValue = this.isMuted ? 0 : 75
        },
        _handleLoaded() {
            if (this.audio.readyState >= 2) {
                if (this.autoPlay) this.play()

                this.loaded = true
                this.totalDuration = parseInt(this.audio.duration)
            } else {
                throw new Error('Failed to load sound file')
            }
        },
        _handlePlayingUI(e) {
            let currTime = parseInt(this.audio.currentTime)
            let percentage = parseInt((currTime / this.totalDuration) * 100)
            this.progressStyle = `width:${percentage}%;`
            this.currentTime = convertTimeHHMMSS(currTime)
        },
        _handlePlayPause(e) {
            if (e.type === 'pause' && this.playing === false) {
                this.progressStyle = `width:0%;`
                this.currentTime = '00:00'
                this.paused = true
            }
        },
        _handleEnded(e) {
            this.stop()
        },
        init() {
            this.audio.addEventListener('ended', this._handleEnded)
            this.audio.addEventListener('timeupdate', this._handlePlayingUI)
            this.audio.addEventListener('loadeddata', this._handleLoaded)
            this.audio.addEventListener('pause', this._handlePlayPause)
            this.audio.addEventListener('play', this._handlePlayPause)
        },
        getAudio() {
            return this.$el.querySelectorAll('audio')[0]
        }
    },
    mounted() {
        this.uuid = generateUUID()
        this.audio = this.getAudio()
        this.innerLoop = this.loop
        this.init()
    },
    beforeDestroy() {
        this.audio.removeEventListener('ended', this._handleEnded)
        this.audio.removeEventListener('timeupdate', this._handlePlayingUI)
        this.audio.removeEventListener('loadeddata', this._handleLoaded)
        this.audio.removeEventListener('pause', this._handlePlayPause)
        this.audio.removeEventListener('play', this._handlePlayPause)
    }
}
</script>

<style scoped>
.oc-audio-wrapper {
    padding:0; margin: 0;
    width:143px;
}
.oc-audio-wrapper.additionalOptions {
    width:263px;
}
    .oc-audio-wrapper .oc-audio__player {
        height: 24px;
        line-height: 24px;
        padding:0; margin: 0;
    }
    .oc-audio-wrapper .oc-audio__player a {
        cursor: pointer;
        display: inline-block;
        vertical-align: middle;
    }
    .oc-audio-wrapper .oc-audio__extern-wrapper {
        display: inline-block;
    }
        .oc-audio-wrapper .oc-audio__extern-wrapper .volume-toggle {
            position: relative;
        }
        .oc-audio-wrapper .oc-audio__extern-wrapper .volume-toggle .volume-slider {
            cursor: pointer;
            position: absolute;
            top: 0;
            width: 80px;
            left: 24px;
        }
    .oc-audio-wrapper .oc-audio__playback-time-wrapper {
        position: relative;
        display: inline-block;
        color: #000;
        cursor: pointer;
        vertical-align: middle;

        padding: 1px 5px;
        font-size: 15px;
        line-height: 1.5;
        float:left;
        z-index: 0;
    }
        .oc-audio-wrapper .oc-audio__playback-time-wrapper .oc-audio__playback-time-separator::after {
            content:' : ';
        }
        .oc-audio-wrapper .oc-audio__playback-time-wrapper .oc-audio__playback-time-indicator {
            background: #c5c5c5;
            height: 22px;
            position: absolute;
            z-index: -1;
            left: 0;
        }

    .oc-audio-btn {
        float:left;
        width: 24px;
        height: 24px;
        text-align: center;
        font-size:10px;
        line-height: 24px;
        border-right: solid 1px #6c757d;
    }

    .oc-audio-wrapper .oc-audio__extern-wrapper .oc-audio-btn {
        font-size:12px;
    }
</style>
